// B4-ITEM-TEMPLATES: the board settings sheet's "Templates" tab — the board's item templates,
// for whoever may edit the board. A quick "+" (New template) and, per row as hover icons, Edit
// (name, when to use it, the task title, priority and checklist; its description, fields and
// tags stay as saved) and Archive (tasks made from it stay). A template a digital employee saved
// is labelled DE. Saving one from a task is the task's ⋯ menu (item-templates.tsx).
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Textarea,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArchiveIcon, PencilIcon, PlusIcon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../../api/client";
import { problemText } from "../../settings/problem-text";
import {
  type ItemTemplate,
  itemTemplateKeys,
  itemTemplatesQuery,
  templateSummary,
} from "../item-templates";
import { PRIORITIES } from "../model";
import { type BoardSettingsTabProps, registerBoardSettingsTab } from "./registry";

const isDe = (t: ItemTemplate) => t.actor_kind === "digital_employee";

/** New or edit: name, note, the task title, priority and checklist (the rest is kept as saved). */
function TemplateDialog({
  api,
  co,
  boardId,
  template,
  onClose,
}: {
  api: Api;
  co: string;
  boardId: string;
  template: ItemTemplate | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const ids = { name: useId(), note: useId(), title: useId(), priority: useId(), list: useId() };
  const p = template?.payload;
  const [name, setName] = useState(template?.name ?? "");
  const [note, setNote] = useState(template?.description ?? "");
  const [title, setTitle] = useState(p?.title ?? "");
  const [priority, setPriority] = useState(String(p?.priority ?? 0));
  const [checklist, setChecklist] = useState((p?.checklist ?? []).join("\n"));
  const save = useMutation({
    mutationFn: () => {
      const items = checklist
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      const { checklist: _c, priority: _p, ...rest } = p ?? { title: "" };
      const payload = {
        ...rest,
        title: title.trim(),
        ...(Number(priority) > 0 ? { priority: Number(priority) } : {}),
        ...(items.length ? { checklist: items } : {}),
      };
      const body = { name: name.trim(), description: note.trim() || null, payload };
      return template
        ? api.client.itemTemplates.update({
            params: { co, id: boardId, sub_id: template.id },
            body,
          })
        : api.client.itemTemplates.create({ params: { co, id: boardId }, body });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: itemTemplateKeys.list(co, boardId) });
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim() && title.trim()) save.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent className="sm:max-w-md" data-slot="template-dialog">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{template ? `Edit ${template.name}` : "New template"}</DialogTitle>
            <DialogDescription>
              {template ? "Its description, fields and tags stay as saved." : "For this board."}
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={ids.name}>Name</FieldLabel>
            <Input
              id={ids.name}
              required
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={ids.note} className="flex items-center gap-1">
              When to use
              <InfoTip label="About the note">Shown next to the template when picking one.</InfoTip>
            </FieldLabel>
            <Input
              id={ids.note}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-[1fr_8rem]">
            <Field>
              <FieldLabel htmlFor={ids.title}>Task title</FieldLabel>
              <Input
                id={ids.title}
                required
                maxLength={500}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.priority}>Priority</FieldLabel>
              <NativeSelect
                id={ids.priority}
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                {PRIORITIES.map((x) => (
                  <NativeSelectOption key={x.value} value={String(x.value)}>
                    {x.label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor={ids.list} className="flex items-center gap-1">
              Checklist
              <InfoTip label="About the checklist">One item per line.</InfoTip>
            </FieldLabel>
            <Textarea
              id={ids.list}
              rows={4}
              value={checklist}
              onChange={(e) => setChecklist(e.target.value)}
            />
          </Field>
          {save.isError ? (
            <Notice tone="danger" role="alert">
              {problemText(save.error, "The template was not saved.")}
            </Notice>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || !name.trim() || !title.trim()}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function TemplatesTab({ board, api, co }: BoardSettingsTabProps) {
  if (!api || !co) return null;
  return <TemplatesList api={api} co={co} boardId={board.id} live={!board.archived_at} />;
}

function TemplatesList({
  api,
  co,
  boardId,
  live,
}: {
  api: Api;
  co: string;
  boardId: string;
  live: boolean;
}) {
  const qc = useQueryClient();
  const list = useQuery(itemTemplatesQuery(api, co, boardId));
  const [editing, setEditing] = useState<ItemTemplate | "new" | null>(null);
  const [archiving, setArchiving] = useState<ItemTemplate | null>(null);
  const archive = useMutation({
    mutationFn: (t: ItemTemplate) =>
      api.client.itemTemplates.archive({ params: { co, id: boardId, sub_id: t.id } }),
    onSuccess: async (_r, t) => {
      toast.add({ title: `Template “${t.name}” archived` });
      setArchiving(null);
      await qc.invalidateQueries({ queryKey: itemTemplateKeys.list(co, boardId) });
    },
  });
  const rows = list.data?.data ?? [];
  return (
    <div data-tab="item-templates" className="flex flex-col gap-2 text-sm">
      <div className="flex items-center gap-1">
        <p className="flex flex-1 items-center gap-1 text-xs font-medium text-muted-foreground">
          Task templates
          <InfoTip label="About task templates">
            New tasks on this board can start from one. Save one from a task's ⋯ menu.
          </InfoTip>
        </p>
        {live ? (
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="New template"
            title="New template"
            onClick={() => setEditing("new")}
          >
            <PlusIcon aria-hidden="true" />
          </Button>
        ) : null}
      </div>
      {rows.length ? (
        <ul aria-label="Task templates" className="m-0 flex list-none flex-col p-0">
          {rows.map((t) => (
            <li
              key={t.id}
              data-template={t.id}
              className="group/tpl flex min-h-9 items-center gap-2 rounded-md px-1 hover:bg-muted"
            >
              <span className="flex min-w-0 flex-1 flex-col py-1">
                <span className="truncate">{t.name}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {[t.payload.title, templateSummary(t)].filter(Boolean).join(" · ")}
                </span>
              </span>
              {isDe(t) ? (
                <Badge variant="info" title="Saved by a digital employee">
                  DE
                </Badge>
              ) : null}
              {live ? (
                <span className="flex items-center opacity-0 group-hover/tpl:opacity-100 group-focus-within/tpl:opacity-100 pointer-coarse:opacity-100">
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`Edit ${t.name}`}
                    title="Edit"
                    onClick={() => setEditing(t)}
                  >
                    <PencilIcon aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`Archive ${t.name}`}
                    title="Archive"
                    onClick={() => setArchiving(t)}
                  >
                    <ArchiveIcon aria-hidden="true" />
                  </Button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : list.isPending ? null : (
        <p className="text-muted-foreground">No templates yet.</p>
      )}
      {editing ? (
        <TemplateDialog
          api={api}
          co={co}
          boardId={boardId}
          template={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {archiving ? (
        <Dialog open onOpenChange={(o) => !o && setArchiving(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Archive {archiving.name}?</DialogTitle>
              <DialogDescription>Tasks made from it stay as they are.</DialogDescription>
            </DialogHeader>
            {archive.isError ? (
              <Notice tone="danger" role="alert">
                {problemText(archive.error, "The template was not archived.")}
              </Notice>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setArchiving(null)}>
                Keep it
              </Button>
              <Button
                type="button"
                disabled={archive.isPending}
                onClick={() => archive.mutate(archiving)}
              >
                Archive
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

const off = registerBoardSettingsTab({
  id: "item-templates",
  title: "Templates",
  order: 25,
  Component: TemplatesTab,
  when: (p) => Boolean(p.api && p.co) && p.can.edit,
});
// A hot reload re-runs this module: the old registration goes first (no duplicate id).
import.meta.hot?.dispose(off);
